import { MouseEvent, useEffect, useMemo, useState } from "react";
import Modal from "../../../components/Modal/Modal";
import CloseIcon from "../../../assets/svg/close-icon";
import ChevronDownIcon from "../../../assets/svg/chevron-down-icon";
import StarIcon from "../../../assets/svg/star-icon";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../../components/Button/Button";
import {
  useSendKamFeaturesRequestMutation,
  useSendKamModalitiesRequestMutation,
} from "../../../redux/services/kamApi";
import { customToast } from "../../../utils/custom-toast/custom-toast";

type RequestItem = {
  code?: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  title: string;
  description: string;
  badgeText?: string;
  isLive?: boolean;
};

interface GetMoreRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  items: RequestItem[];
  showViewMore?: boolean;
  submitApiType?: "modalities" | "features";
}

const VISIBLE_ITEMS_COUNT = 5;

export const GetMoreRequestModal = ({
  open,
  onOpenChange,
  title,
  description,
  items,
  showViewMore = false,
  submitApiType,
}: GetMoreRequestModalProps) => {
  const [checkedState, setCheckedState] = useState<boolean[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sendKamModalitiesRequest, { isLoading: isSubmittingModalitiesRequest }] =
    useSendKamModalitiesRequestMutation();
  const [sendKamFeaturesRequest, { isLoading: isSubmittingFeaturesRequest }] =
    useSendKamFeaturesRequestMutation();

  useEffect(() => {
    setCheckedState(items.map((item) => Boolean(item.isLive)));
  }, [items, open]);

  useEffect(() => {
    if (!open) {
      setIsExpanded(false);
    }
  }, [open]);

  const sortedItems = useMemo(() => {
    return items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort((a, b) => Number(Boolean(b.item.isLive)) - Number(Boolean(a.item.isLive)));
  }, [items]);

  const hasViewMore = showViewMore && sortedItems.length > VISIBLE_ITEMS_COUNT;
  const visibleItems = useMemo(() => {
    if (!hasViewMore || isExpanded) {
      return sortedItems;
    }
    return sortedItems.slice(0, VISIBLE_ITEMS_COUNT);
  }, [sortedItems, hasViewMore, isExpanded]);

  const handleCloseModal = () => {
    onOpenChange(false);
  };

  const handleItemCheckToggle = (event: MouseEvent<HTMLButtonElement>) => {
    const indexValue = event.currentTarget.dataset.index;
    const index = indexValue ? Number(indexValue) : NaN;
    if (Number.isNaN(index)) {
      return;
    }
    if (items[index]?.isLive) {
      return;
    }
    setCheckedState((prevState) =>
      prevState.map((isChecked, itemIndex) =>
        itemIndex === index ? !isChecked : isChecked,
      ),
    );
  };

  const handleViewMoreToggle = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const handleCancelClick = () => {
    handleCloseModal();
  };

  const handleSubmitRequestClick = async () => {
    if (!submitApiType) {
      handleCloseModal();
      return;
    }

    const newlySelectedItems = items.filter(
      (item, index) => checkedState[index] && !item.isLive,
    );
    const selectedCodes = newlySelectedItems
      .map((item) => item.code)
      .filter((code): code is string => Boolean(code));

    if (selectedCodes.length === 0) {
      return;
    }

    try {
      if (submitApiType === "features") {
        await sendKamFeaturesRequest({
          feature_codes: selectedCodes,
        }).unwrap();
        customToast("Feature request submitted successfully", "success");
      } else {
        await sendKamModalitiesRequest({
          modality_codes: selectedCodes,
        }).unwrap();
        customToast("Modality request submitted successfully", "success");
      }
      handleCloseModal();
    } catch (error) {
      const errorMessage =
        (error as { data?: { detail?: string; message?: string } })?.data
          ?.detail ||
        (error as { data?: { detail?: string; message?: string } })?.data
          ?.message ||
        `Failed to submit ${submitApiType} request`;
      customToast(errorMessage, "error");
    }
  };

  const isSubmittingRequest =
    isSubmittingModalitiesRequest || isSubmittingFeaturesRequest;
  const allItemsChecked =
    items.length > 0 &&
    items.every((item, index) => !item.code || Boolean(checkedState[index]));
  const hasNewSelection = items.some(
    (item, index) => Boolean(item.code) && !item.isLive && checkedState[index],
  );
  const isSubmitDisabled = isSubmittingRequest || !hasNewSelection;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={false}
      showCloseButton={false}
      contentClassName="bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg w-[540px] shadow-popper-blur-effect backdrop-blur-2xl !z-[99999999]"
      overlayClassName="!bg-black/60 !z-[9999999]"
      bodyClassName="!px-6 !py-5"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              {title}
            </span>
            <span className="text-sm text-data dark:text-data-dk">
              {description}
            </span>
          </div>
          <button
            type="button"
            className="w-[30px] h-[30px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex items-center justify-center"
            onClick={handleCloseModal}
          >
            <CloseIcon className="w-[12px] h-[12px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto pr-1">
          {visibleItems.map(({ item, originalIndex }) => {
            const Icon = item.icon;
            const isChecked = Boolean(checkedState[originalIndex]);
            const isLockedLiveItem = Boolean(item.isLive);

            return (
              <div
                key={`${item.title}-${originalIndex}`}
                className={`relative flex items-center justify-between gap-3 p-3 rounded-lg border transition-all ${
                  isChecked
                    ? "border-selected-option-highlight dark:border-selected-option-highlight-dk bg-card-highlight dark:bg-card-highlight-dk"
                    : "border-border-gray dark:border-border-gray-dk"
                }`}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Icon className={`${item.iconColor} w-5 h-5 mt-0.5 shrink-0`} />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
                      {item.title}
                    </span>
                    <span className="text-xs text-data dark:text-data-dk">
                      {item.description}
                    </span>
                  </div>
                </div>

                <div className="relative shrink-0 group/checkbox">
                  <button
                    type="button"
                    data-index={originalIndex}
                    className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                      isChecked
                        ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk"
                        : "border-gray-placeholder dark:border-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk"
                    } ${isLockedLiveItem ? "cursor-not-allowed opacity-80" : ""}`}
                    onClick={handleItemCheckToggle}
                    aria-label={isLockedLiveItem ? "Already subscribed" : undefined}
                    aria-disabled={isLockedLiveItem}
                  >
                    {isChecked && (
                      <CheckIcon className="w-4 h-4 text-pure-white dark:text-pure-white-dk" />
                    )}
                  </button>
                  {isLockedLiveItem && (
                    <span className="pointer-events-none absolute right-0 -top-7 z-10 whitespace-nowrap rounded bg-heading-text dark:bg-heading-text-dk px-2 py-1 text-xxs font-medium text-pure-white dark:text-pure-white-dk opacity-0 shadow-sm transition-opacity duration-150 group-hover/checkbox:opacity-100">
                      Already subscribed
                    </span>
                  )}
                </div>

                {item.badgeText && (
                  <span className="absolute top-2 right-9 flex items-center gap-1 py-1 px-1.5 rounded text-xs font-semibold text-verba-sky dark:text-verba-sky-dk border border-border-gray dark:border-border-gray-dk">
                    <StarIcon className="w-3.5 h-3.5 text-rating-yellow dark:text-rating-yellow-dk" />
                    <span>{item.badgeText}</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            className={`flex items-center gap-1 text-md font-semibold text-verba-blue dark:text-verba-blue-dk hover:underline ${
              hasViewMore ? "visible" : "invisible"
            }`}
            onClick={handleViewMoreToggle}
          >
            {isExpanded ? "View Less" : "View More"}
            <ChevronDownIcon
              className={`w-3 h-3 text-verba-blue dark:text-verba-blue-dk transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </button>

          <div className="flex gap-3 items-center">
            <Button
              innerText="Cancel"
              buttonContainerClass="w-[100px] h-[32px] border border-border-gray dark:border-border-gray-dk rounded-lg"
              innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
              onClick={handleCancelClick}
            />
            <Button
              innerText={isSubmittingRequest ? "Submitting..." : "Submit Request"}
              buttonContainerClass={`w-[145px] h-[32px] rounded-lg ${
                isSubmitDisabled
                  ? "bg-gray-100 dark:bg-gray-100-dk border border-border-gray dark:border-border-gray-dk opacity-50 cursor-not-allowed"
                  : "bg-verba-indigo dark:bg-pure-white dark:border dark:border-border-gray-dk"
              }`}
              innerTextClass={`text-md font-semibold ${
                isSubmitDisabled
                  ? "text-gray-placeholder dark:text-black-dk"
                  : "text-fixed-white dark:text-[#1a1a1a]"
              }`}
              onClick={handleSubmitRequestClick}
              disabled={isSubmitDisabled}
              isLoading={isSubmittingRequest}
              loaderColor={isSubmitDisabled ? "text-gray-placeholder dark:text-gray-placeholder-dk" : "text-fixed-white dark:text-[#1a1a1a]"}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
